import { Dispatch, SetStateAction, useCallback, useRef, useLayoutEffect } from 'react';

export const isBrowser = typeof window !== 'undefined';
const noop = () => {};

type ParserOptions<T> = { storage?: Storage } & (
  | {
      raw: true;
    }
  | {
      raw: false;
      serializer: (value: T) => string;
      deserializer: (value: string) => T;
    }
);

/**
 * @source https://github.com/streamich/react-use/blob/master/docs/useLocalStorage.md
 * This is a modified version to use Ref instead of State to avoid rerenders in Context API
 */
const useStorageRef = <T>(
  key: string,
  initialValue: T,
  options?: ParserOptions<T>,
): [() => T, Dispatch<SetStateAction<T>>, (callback: any) => () => boolean, () => void] => {
  if (!isBrowser) {
    return [() => initialValue as T, noop, () => () => false, noop];
  }
  if (!key) {
    throw new Error('useStorageRef key may not be falsy');
  }

  const storage = options?.storage || localStorage;

  const deserializer = options
    ? options.raw
      ? (value: string) => value
      : options.deserializer
    : JSON.parse;

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const initializer = useRef((key: string) => {
    try {
      const serializer = options ? (options.raw ? String : options.serializer) : JSON.stringify;

      const localStorageValue = storage.getItem(key);
      if (localStorageValue !== null) {
        return deserializer(localStorageValue);
      } else {
        initialValue && storage.setItem(key, serializer(initialValue));
        return initialValue;
      }
    } catch {
      // If user is in private mode or has storage restriction
      // localStorage can throw. JSON.parse and JSON.stringify
      // can throw, too.
      return initialValue;
    }
  });

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const stateRef = useRef<T>(initializer.current(key));

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const get = useCallback(() => stateRef.current, []);

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const subscribers = useRef(new Set());

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const subscribe = useCallback((callback: any) => {
    subscribers.current.add(callback);
    return () => subscribers.current.delete(callback);
  }, []);

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const set: Dispatch<SetStateAction<T>> = useCallback(
    valOrFunc => {
      try {
        const newState =
          typeof valOrFunc === 'function' ? (valOrFunc as Function)(stateRef.current) : valOrFunc;
        if (typeof newState === 'undefined') return;
        let value: string;

        if (options)
          if (options.raw)
            if (typeof newState === 'string') value = newState;
            else value = JSON.stringify(newState);
          else if (options.serializer) value = options.serializer(newState);
          else value = JSON.stringify(newState);
        else value = JSON.stringify(newState);

        storage.setItem(key, value);
        stateRef.current = deserializer(value);
        return subscribers.current.forEach((callback: any) => callback());
      } catch {
        // If user is in private mode or has storage restriction
        // localStorage can throw. Also JSON.stringify can throw.
      }
    },
    [key, storage],
  );

  // eslint-disable-next-line react-hooks/rules-of-hooks
  useLayoutEffect(() => set(initializer.current(key)), [key, set]);

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const remove = useCallback(() => {
    try {
      storage.removeItem(key);
      stateRef.current = initialValue;
      return subscribers.current.forEach((callback: any) => callback());
    } catch {
      // If user is in private mode or has storage restriction
      // localStorage can throw.
    }
  }, [key, storage]);

  return [get, set, subscribe, remove];
};

export default useStorageRef;
